import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import { Database, HardDrive, Download, CheckCircle2, Loader2, AlertCircle } from 'lucide-react'
import { backupsAPI } from '../services/backupsAPI'

export default function DataBackup() {
  const [activeTab, setActiveTab] = useState('full')
  const [downloadingPath, setDownloadingPath] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingBackupType, setPendingBackupType] = useState(null) // 'full' or 'differential'
  const queryClient = useQueryClient()

  // Fetch backup stats - optimized caching
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['backup-stats'],
    () => backupsAPI.getStats(),
    {
      staleTime: 1 * 60 * 1000, // 1 minute (backups change frequently)
      cacheTime: 5 * 60 * 1000,
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnMount: false,
    }
  )

  // Fetch backup list - optimized caching
  const { data: backupsData, isLoading: backupsLoading } = useQuery(
    ['backups', activeTab],
    () => backupsAPI.getAll(activeTab === 'full' ? 'full' : activeTab === 'differential' ? 'differential' : 'all'),
    {
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000,
      refetchInterval: 30000,
      refetchOnMount: false,
    }
  )

  // Create full backup mutation
  const createFullBackupMutation = useMutation(
    () => backupsAPI.createFull(),
    {
      onSuccess: (response) => {
        toast.success('Full backup created successfully!')
        queryClient.invalidateQueries(['backup-stats'])
        queryClient.invalidateQueries(['backups'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create full backup')
      }
    }
  )

  // Create differential backup mutation
  const createDifferentialBackupMutation = useMutation(
    () => backupsAPI.createDifferential(),
    {
      onSuccess: (response) => {
        toast.success('Differential backup created successfully!')
        queryClient.invalidateQueries(['backup-stats'])
        queryClient.invalidateQueries(['backups'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create differential backup')
      }
    }
  )

  const handleCreateFullBackup = () => {
    setPendingBackupType('full')
    setShowConfirmModal(true)
  }

  const handleCreateDifferentialBackup = () => {
    setPendingBackupType('differential')
    setShowConfirmModal(true)
  }

  const handleConfirmBackup = () => {
    setShowConfirmModal(false)
    if (pendingBackupType === 'full') {
      createFullBackupMutation.mutate()
    } else if (pendingBackupType === 'differential') {
      createDifferentialBackupMutation.mutate()
    }
    setPendingBackupType(null)
  }

  const handleCancelBackup = () => {
    setShowConfirmModal(false)
    setPendingBackupType(null)
  }

  const handleDownloadBackup = async (filePath) => {
    if (!filePath) return
    try {
      setDownloadingPath(filePath)
      const response = await backupsAPI.getDownloadUrl(filePath)
      const downloadUrl = response?.data?.data?.url
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer')
        toast.success('Download link generated')
      } else {
        toast.error('Unable to generate download link')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate download link')
    } finally {
      setDownloadingPath(null)
    }
  }

  const stats = statsData?.data?.data
  const backups = backupsData?.data?.data?.backups || []

  const tabs = [
    { id: 'full', name: 'Full Backup', icon: HardDrive },
    { id: 'differential', name: 'Differential Backup', icon: Database }
  ]

  const formatFileSize = size => {
    if (!size) return '0 Bytes'
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const index = Math.floor(Math.log(size) / Math.log(1024))
    return `${(size / Math.pow(1024, index)).toFixed(2)} ${units[index]}`
  }

  const renderBackupTable = (title) => (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {!backupsLoading && (
          <span className="text-xs text-secondary-500">{backups.length} backup{backups.length === 1 ? '' : 's'}</span>
        )}
      </div>
      {backupsLoading ? (
        <div className="py-6 flex justify-center">
          <LoadingSpinner size="md" />
        </div>
      ) : backups.length === 0 ? (
        <div className="py-6 text-center text-secondary-500 text-sm bg-secondary-50 rounded-md">
          No backups found. Create a {title.toLowerCase()} to see it here.
        </div>
      ) : (
        <div className="overflow-x-auto border border-secondary-200 rounded-lg">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Storage Path</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {backups.map((backup) => {
                const filePath = backup.path || backup.name
                return (
                  <tr key={backup.name}>
                    <td className="px-4 py-3 text-sm text-secondary-900">{backup.name}</td>
                    <td className="px-4 py-3 text-sm text-secondary-600">
                      {new Date(backup.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-600">{formatFileSize(backup.size)}</td>
                    <td className="px-4 py-3 text-sm text-secondary-600 truncate max-w-xs" title={backup.cloudPath}>
                      {backup.cloudPath}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDownloadBackup(filePath)}
                        disabled={downloadingPath === filePath}
                        className="inline-flex items-center px-3 py-1.5 bg-white border border-secondary-200 rounded-md text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingPath === filePath ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Preparing...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelBackup}
        onConfirm={handleConfirmBackup}
        title={pendingBackupType === 'full' ? 'Create Full Backup' : 'Create Differential Backup'}
        message={
          pendingBackupType === 'full'
            ? 'Are you sure you want to create a full backup? This may take a few minutes.'
            : 'Are you sure you want to create a differential backup?'
        }
        confirmText="OK"
        cancelText="Cancel"
        type="info"
      />

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Data Backup</h1>
        <p className="text-gray-600 mt-1">Manage and create data backups</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {activeTab === 'full' ? (
              <>
                <HardDrive className="h-5 w-5 mr-2" />
                Full Backup
              </>
            ) : (
              <>
                <Database className="h-5 w-5 mr-2" />
                Differential Backup
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeTab === 'full' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Full Backup</h3>
                <p className="text-gray-600 mb-4">
                  A full backup creates a complete copy of all your data. This includes all databases, 
                  files, and configurations. Full backups are comprehensive but may take longer to complete.
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">Backup Information</h4>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Includes all data and configurations</li>
                          <li>Complete system snapshot</li>
                          <li>Recommended for initial backups</li>
                          <li>Can be used for complete system restoration</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <button 
                    onClick={handleCreateFullBackup}
                    disabled={createFullBackupMutation.isLoading}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createFullBackupMutation.isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Backup...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Create Full Backup
                      </>
                    )}
                  </button>
                </div>

                {/* Backup Stats */}
                {stats && (
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-secondary-50 rounded-lg p-4">
                      <div className="text-sm text-secondary-600">Total Backups</div>
                      <div className="text-2xl font-bold text-secondary-900">{stats.total?.count || 0}</div>
                      <div className="text-xs text-secondary-500 mt-1">{stats.total?.sizeFormatted || '0 Bytes'}</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-600">Full Backups</div>
                      <div className="text-2xl font-bold text-blue-900">{stats.full?.count || 0}</div>
                      <div className="text-xs text-blue-500 mt-1">{stats.full?.sizeFormatted || '0 Bytes'}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-green-600">Differential Backups</div>
                      <div className="text-2xl font-bold text-green-900">{stats.differential?.count || 0}</div>
                      <div className="text-xs text-green-500 mt-1">{stats.differential?.sizeFormatted || '0 Bytes'}</div>
                    </div>
                  </div>
                )}

                {renderBackupTable('Full Backups')}
              </div>
            </div>
          )}

          {activeTab === 'differential' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Differential Backup</h3>
                <p className="text-gray-600 mb-4">
                  A differential backup only includes data that has changed since the last full backup. 
                  This makes differential backups faster and smaller, but you'll need both the full backup 
                  and all subsequent differential backups to restore your system.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-green-900">Backup Information</h4>
                      <div className="mt-2 text-sm text-green-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Only includes changes since last full backup</li>
                          <li>Faster and smaller than full backups</li>
                          <li>Requires the original full backup to restore</li>
                          <li>Ideal for regular scheduled backups</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {stats?.full?.count === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-yellow-900">No Full Backup Found</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          You need to create a full backup before creating a differential backup.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex items-center space-x-4">
                  <button 
                    onClick={handleCreateDifferentialBackup}
                    disabled={createDifferentialBackupMutation.isLoading || stats?.full?.count === 0}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createDifferentialBackupMutation.isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Backup...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Create Differential Backup
                      </>
                    )}
                  </button>
                </div>

                {renderBackupTable('Differential Backups')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

